/**
 * copy retry code from https://stackoverflow.com/a/53945649/8842357
 */
import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, ReplaySubject, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';

import {APP_BUILD, APP_PLATFORM, APP_VERSION} from '../../defines/client.const';
import {GUKEO_API_URL1} from "../../defines/protocol";
import {IsEmptyString} from '../../defines/common.func';
import {environment} from "../../../../environments/environment";
import {UserService} from '../user/user.service';
import {LayoutMgrService} from '../layout-mgr/layout-mgr.service';
import {CordovaService} from "../cordova/cordova.service";


@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {

  constructor(
    private userService: UserService,
    private cordovaSvc: CordovaService,
    private lm: LayoutMgrService,
  ) {
  }

  // tslint:disable-next-line:variable-name
  private _refreshSubject = new ReplaySubject<any>(1);

  private _ifTokenExpired(): ReplaySubject<any> {
    const refreshSubject = this._refreshSubject;
    refreshSubject.subscribe({
      error: () => {
        if (this._refreshSubject === refreshSubject) {
          this._refreshSubject = new ReplaySubject<any>(1);
        }
      },
      complete: () => {
        if (this._refreshSubject === refreshSubject) {
          this._refreshSubject = new ReplaySubject<any>(1);
        }
      }
    });
    if (refreshSubject.observers.length === 1) {
      this.userService.Login(true).subscribe(refreshSubject);
    }
    return refreshSubject;
  }

  private _checkTokenExpiryError(error: HttpErrorResponse): boolean {
    return (
      error.status !== 0
      && error.status === 401
      // && error.error.message === 'TokenExpired'
      && (error.error.detail === '토큰이 유효하지 않습니다.'  // TODO: [주의] 서버의 로케일이 변경되면 문제가 될 수 있다.
      || error.error.detail === '자격 인증데이터(authentication credentials)가 제공되지 않았습니다.')
    );
  }

  private getBaseHeaders(): { [name: string]: string } {
    return {
      'app-ver': APP_BUILD,
      'platform': this.cordovaSvc.platform ?? APP_PLATFORM,
    };
  }

  private updateHeader(req: HttpRequest<any>): HttpRequest<any> {
    const headers = {
      ...this.getBaseHeaders(),
      Authorization: `Token ${this.userService.Token}`
    };
    
    return req.clone({ setHeaders: headers });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // GUKEO_API_URL1로 시작하지 않는 요청만 인터셉터를 건너뜁니다
    if (!req.url.startsWith(GUKEO_API_URL1)) {
      return next.handle(req);
    }

    let request: HttpRequest<any>;
    
    // 토큰이 필요한 API인지 확인 (등록/로그인이 아닌 경우)
    const needsToken = !(req.url.endsWith('/register/') || req.url.endsWith('/login/'));

    // 최초 등록·로그인이 필요하거나 진행 중이면 보호 API를 먼저 보내지 않고 로그인 흐름을 공유합니다.
    if (needsToken && IsEmptyString(this.userService.Token)) {
      return this.userService.Login().pipe(
        switchMap(loginSuccess => {
          if (!loginSuccess || IsEmptyString(this.userService.Token)) {
            return throwError(() => new Error('인증 토큰을 발급받지 못했습니다.'));
          }
          return this.intercept(req, next);
        })
      );
    }
    
    // 토큰이 필요하고 토큰이 있는 경우에만 토큰 포함
    if (needsToken && !IsEmptyString(this.userService.Token)) {
      request = this.updateHeader(req);
    } else {
      // 그 외의 경우는 기본 헤더만 포함
      request = req.clone({ setHeaders: this.getBaseHeaders() });
    }
    
    // console.warn('intercept() request =', request);

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error instanceof HttpErrorResponse) {

          if (needsToken && this._checkTokenExpiryError(error)) {
            return this._ifTokenExpired().pipe(
              switchMap(loginSuccess => {
                if (!loginSuccess || IsEmptyString(this.userService.Token)) {
                  return throwError(() => new Error('인증 토큰을 갱신하지 못했습니다.'));
                }
                return next.handle(this.updateHeader(request));
              })
            );
          } else {
            this.openErrorPopupIf(error);
            return throwError(() => error);
          }

        }
        return throwError(() => error);
      })
    );
  }

  private openErrorPopupIf(error: HttpErrorResponse): void {
    const httpError = error as HttpErrorResponse;

    // httpError.status 에 429 오류가 안 들어오는 이유.
    // GCP Cloud Armor 에서 429 오류를 발생시키는 경우에 httpError.status 거 0으로 들어온다.
    // 429 오류 내에서 적절한 헤더가 설정 안되어서 CORS가 발행하고 그래서 429 오류 응답 자체가 브라우저에의해서 차단 되는 것으로 보임.
    if (httpError.status === 0) {
      // 네트워크 연결 관련 오류 메시지
      const message = "서버에서 응답이 없습니다.<br/>인터넷 연결을 확인해 주세요.";
      this.lm.OpenMsgPopupAlert(message, '네트워크 오류').subscribe(data => console.log(data));
    }
    else if (httpError.status !== 404 &&    // 404(Not Found)는 결과가 없다는 의미이므로 무시
      // (!environment.production || httpError.status !== 400))  // 상용 모드가 아닐 때만 400(Bad Request)는 오류 표시
      !environment.production)  // 상용 모드가 아닐 때만 오류 표시
    {
      const msg = httpError.message;
      const detail = httpError.error.detail;
      const message = `Server Error<br/>status:${httpError.status}<br/><br/>message:${msg}<br/><br/>detail:${detail}<br/><br/>ver: ${APP_VERSION}<br/>user: ${this.userService.UserId}`;
      // alert(message);
      this.lm.OpenMsgPopupAlert(message, '네트워크 오류').subscribe(data => console.log(data));
    }
  }
}
